import React, { useEffect, useRef, useState } from 'react';
import { Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, ChevronDown, Upload, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  touRatesAdminService, type TouRateRow, type TouChannel, type BulkUploadResponse,
} from '../../services/touRatesAdminService';

const PAGE_SIZE = 25;
const STATES = ['', 'QLD', 'VIC', 'NSW', 'SA', 'ACT', 'TAS'];
const RATE_TYPES = ['Super Peak', 'Peak', 'Shoulder', 'Off Peak', 'N/A'];

// NEM12 channel (migration 116). E windows classify consumption; B windows are
// solar feed-in and are never used to band general usage.
const CHANNEL_OPTIONS: { value: TouChannel; label: string }[] = [
  { value: 'E', label: 'E — General usage' },
  { value: 'B', label: 'B — Solar feed-in' },
];

const DAY_OPTIONS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const formatWeekdays = (days: number[] | null): string => {
  if (!days?.length) return '—';
  return days.join(', ');
};

const formatHolidayNames = (names: string[] | null): string => {
  if (!names?.length) return '—';
  return names.join(', ');
};

const EMPTY_FORM: Partial<TouRateRow> = {
  retailer_name: '',
  state: 'QLD',
  channel: 'E',
  rate_type: 'Peak',
  time_range: '',
  weekdays: [0, 1, 2, 3, 4],
  holiday_names: [],
  source_nmi: '',
  last_updated: '',
  notes: '',
  start_time: '',
  end_time: '',
};

const RATE_TYPE_COLOURS: Record<string, string> = {
  'Super Peak': 'bg-red-100 text-red-800',
  Peak: 'bg-orange-100 text-orange-800',
  Shoulder: 'bg-yellow-100 text-yellow-800',
  'Off Peak': 'bg-green-100 text-green-800',
  'N/A': 'bg-gray-100 text-gray-600',
};

export const TouRatesManagement: React.FC = () => {
  const [rows, setRows] = useState<TouRateRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [retailerFilter, setRetailerFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState<TouChannel | ''>('');

  const [retailers, setRetailers] = useState<string[]>([]);
  const [holidayNameOptions, setHolidayNameOptions] = useState<string[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TouRateRow | null>(null);
  const [form, setForm] = useState<Partial<TouRateRow>>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUploadResponse | null>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  useEffect(() => {
    touRatesAdminService.getRetailers().then(setRetailers);
  }, []);

  useEffect(() => {
    if (!dialogOpen || !form.state) return;
    touRatesAdminService.getHolidayNames(form.state).then((names) => {
      setHolidayNameOptions(names);
      // Drop any previously-selected holiday not valid for the (possibly just-changed) state.
      setForm((f) => {
        const kept = (f.holiday_names ?? []).filter((n) => names.includes(n));
        if (kept.length === (f.holiday_names ?? []).length) return f;
        return { ...f, holiday_names: kept };
      });
    });
  }, [dialogOpen, form.state]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [retailerFilter, stateFilter, channelFilter]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    touRatesAdminService.list({ search, retailer: retailerFilter, state: stateFilter, channel: channelFilter, page, limit: PAGE_SIZE })
      .then((res) => {
        if (!active) return;
        if (!res.success) { setError(res.error ?? 'Failed to load'); setRows([]); setCount(0); }
        else { setRows(res.data ?? []); setCount(res.count ?? 0); }
        setLoading(false);
      });
    return () => { active = false; };
  }, [search, retailerFilter, stateFilter, channelFilter, page]);

  const refresh = () => {
    setLoading(true);
    touRatesAdminService.list({ search, retailer: retailerFilter, state: stateFilter, channel: channelFilter, page, limit: PAGE_SIZE })
      .then((res) => {
        if (res.success) { setRows(res.data ?? []); setCount(res.count ?? 0); }
        setLoading(false);
      });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: TouRateRow) => {
    setEditing(row);
    setForm({
      retailer_name: row.retailer_name,
      state: row.state,
      channel: row.channel ?? 'E',
      rate_type: row.rate_type,
      time_range: row.time_range ?? '',
      weekdays: row.weekdays ?? [1, 2, 3, 4, 5],
      holiday_names: row.holiday_names ?? [],
      source_nmi: row.source_nmi ?? '',
      last_updated: row.last_updated ?? '',
      notes: row.notes ?? '',
      start_time: row.start_time ?? '',
      end_time: row.end_time ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.retailer_name?.trim()) { setFormError('Retailer name is required'); return; }
    if (!form.state) { setFormError('State is required'); return; }
    if (!form.rate_type) { setFormError('Rate type is required'); return; }

    const body: Partial<TouRateRow> = {
      retailer_name: form.retailer_name.trim(),
      state: form.state,
      channel: form.channel ?? 'E',
      rate_type: form.rate_type,
      time_range: form.time_range?.trim() || null,
      weekdays: form.weekdays?.length ? form.weekdays : [1, 2, 3, 4, 5],
      holiday_names: form.holiday_names ?? [],
      source_nmi: form.source_nmi?.trim() || null,
      last_updated: form.last_updated?.trim() || null,
      notes: form.notes?.trim() || null,
      start_time: form.start_time?.trim() || null,
      end_time: form.end_time?.trim() || null,
    };

    setSaving(true);
    setFormError(null);
    const res = editing
      ? await touRatesAdminService.update(editing.id, body)
      : await touRatesAdminService.create(body);
    setSaving(false);

    if (!res.success) { setFormError(res.error ?? 'Save failed'); return; }
    setDialogOpen(false);
    if (!editing) touRatesAdminService.getRetailers().then(setRetailers);
    refresh();
  };

  const handleDelete = async (row: TouRateRow) => {
    if (!window.confirm(`Delete ${row.retailer_name} – ${row.state} – ${row.channel === 'B' ? 'Solar ' : ''}${row.rate_type} ${row.start_time ?? ''}–${row.end_time ?? ''}? This cannot be undone.`)) return;
    const res = await touRatesAdminService.remove(row.id);
    if (!res.success) { setError(res.error ?? 'Delete failed'); return; }
    if (rows.length === 1 && page > 1) setPage((p) => p - 1);
    else refresh();
  };

  const downloadSample = () => {
    const csv = [
      'retailer_name,state,channel,rate_type,start_time,end_time,weekdays,holiday_names,time_range,source_nmi,last_updated,notes',
      'AGL Energy,QLD,E,Peak,07:00,22:00,0|1|2|3|4,Christmas Day|Boxing Day,7am – 10pm,,2025-01-01,',
      'AGL Energy,QLD,E,Off Peak,22:00,07:00,0|1|2|3|4|5|6,Christmas Day|Boxing Day,10pm – 7am,,2025-01-01,',
      "Origin Energy,NSW,E,Peak,07:00,23:00,0|1|2|3|4,New Year's Day,7am – 11pm,,2025-01-01,",
      'Origin Energy,NSW,E,Shoulder,07:00,23:00,5|6,,Sat–Sun 7am – 11pm,,2025-01-01,',
      'Origin Energy,NSW,B,Peak,07:00,09:00,0|1|2|3|4,,Solar feed-in 7am – 9am,,2025-01-01,',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'tou_rates_sample.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const openBulkDialog = () => { setBulkFile(null); setBulkResult(null); setBulkDialogOpen(true); };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkFile(e.target.files?.[0] ?? null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    const result = await touRatesAdminService.bulkUpload(bulkFile);
    setBulkResult(result);
    setBulkUploading(false);
    if (result.success && (result.inserted ?? 0) > 0) {
      touRatesAdminService.getRetailers().then(setRetailers);
      refresh();
    }
  };

  const field = (key: keyof Partial<TouRateRow>) => (
    String(form[key] ?? '')
  );
  const setField = (key: keyof Partial<TouRateRow>, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Retailer TOU Rates</h1>
        <p className="text-sm text-muted-foreground">
          Time-of-use tariff window definitions per retailer and state. Used to classify interval data into Peak, Shoulder, and Off Peak periods.
        </p>
      </div>

      {/* Filters + toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search retailer, NMI, time range…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>

        <select
          value={retailerFilter}
          onChange={(e) => setRetailerFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All retailers</option>
          {retailers.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {STATES.map((s) => <option key={s} value={s}>{s || 'All states'}</option>)}
        </select>

        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value as TouChannel | '')}
          aria-label="Channel"
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All channels</option>
          {CHANNEL_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>

        <span className="text-sm text-muted-foreground">{count.toLocaleString()} rows</span>
        <div className="flex-1" />
        <Button variant="outline" onClick={openBulkDialog} className="gap-1.5">
          <Upload className="h-4 w-4" /> Bulk upload
        </Button>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> Add TOU rate
        </Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Rate Type</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>Weekdays</TableHead>
                <TableHead>Holidays</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No rows found</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.retailer_name}</TableCell>
                  <TableCell><Badge variant="outline">{row.state}</Badge></TableCell>
                  <TableCell>
                    {row.channel === 'B' ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800" title="Solar feed-in (NEM12 channel B)">B · Solar</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700" title="General usage (NEM12 channel E)">E</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${RATE_TYPE_COLOURS[row.rate_type] ?? 'bg-gray-100 text-gray-600'}`}>
                      {row.rate_type}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{row.start_time ?? '—'}</TableCell>
                  <TableCell className="font-mono text-sm">{row.end_time ?? '—'}</TableCell>
                  <TableCell className="text-sm">{formatWeekdays(row.weekdays)}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-sm" title={formatHolidayNames(row.holiday_names)}>
                    {formatHolidayNames(row.holiday_names)}
                  </TableCell>
                  <TableCell>{row.last_updated ?? '—'}</TableCell>
                  <TableCell className="max-w-[120px] truncate text-muted-foreground" title={row.notes ?? ''}>{row.notes ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(row)} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk upload dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!bulkUploading) setBulkDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload TOU Rates</DialogTitle>
            <DialogDescription>
              Upload CSV or XLSX. Required: <strong>retailer_name</strong>, <strong>state</strong>, <strong>rate_type</strong>. Optional <strong>channel</strong>: <code>E</code> (general usage, the default) or <code>B</code> (solar feed-in). Weekdays as pipe-separated integers (0=Mon … 6=Sun), e.g. <code>0|1|2|3|4</code>. Holiday names as pipe-separated values matching the holidays table, e.g. <code>Christmas Day|Boxing Day</code>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <button type="button" onClick={downloadSample} className="flex items-center gap-1.5 text-sm text-primary hover:underline">
              <Download className="h-4 w-4" /> Download sample CSV
            </button>

            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input p-6 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => bulkFileRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              {bulkFile ? (
                <span className="text-sm font-medium">{bulkFile.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">Click to select CSV or XLSX file</span>
              )}
              <input ref={bulkFileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleBulkFileChange} />
            </div>

            {bulkResult && (
              <div className="space-y-2">
                {bulkResult.success ? (
                  <Alert>
                    <AlertDescription>
                      <span className="font-medium">Upload complete</span>
                      <span className="text-sm ml-2">
                        {bulkResult.inserted} inserted · {bulkResult.duplicates ?? 0} already existed · {(bulkResult.errors?.length ?? 0) - (bulkResult.duplicates ?? 0)} errors (of {bulkResult.total} rows)
                      </span>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive"><AlertDescription>{bulkResult.error}</AlertDescription></Alert>
                )}
                {(bulkResult.errors?.length ?? 0) > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/30 p-2 text-xs space-y-1">
                    {bulkResult.errors!.map((e) => (
                      <div key={`${e.row}-${e.error}`} className={e.error.startsWith('Already exists') ? 'text-muted-foreground' : 'text-red-600'}>
                        Row {e.row}: {e.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>
              {bulkResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult?.success && (
              <Button onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}>
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit TOU rate' : 'Add TOU rate'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update the fields and save.' : 'Create a new TOU rate record.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fld-retailer_name">Retailer name <span className="text-red-500">*</span></Label>
              <Input id="fld-retailer_name" value={field('retailer_name')} onChange={(e) => setField('retailer_name', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-state">State <span className="text-red-500">*</span></Label>
              <select
                id="fld-state"
                value={field('state')}
                onChange={(e) => setField('state', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {STATES.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-channel">Channel <span className="text-red-500">*</span></Label>
              <select
                id="fld-channel"
                value={form.channel ?? 'E'}
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value as TouChannel }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {CHANNEL_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-rate_type">Rate type <span className="text-red-500">*</span></Label>
              <select
                id="fld-rate_type"
                value={field('rate_type')}
                onChange={(e) => setField('rate_type', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {RATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-source_nmi">Source NMI</Label>
              <Input id="fld-source_nmi" value={field('source_nmi')} onChange={(e) => setField('source_nmi', e.target.value)} className="font-mono" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-start_time">Start time (24hr)</Label>
              <Input id="fld-start_time" type="time" value={field('start_time')} onChange={(e) => setField('start_time', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-end_time">End time (24hr)</Label>
              <Input id="fld-end_time" type="time" value={field('end_time')} onChange={(e) => setField('end_time', e.target.value)} />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="fld-time_range">Time range (label)</Label>
              <Input id="fld-time_range" placeholder="e.g. 7am – 11pm" value={field('time_range')} onChange={(e) => setField('time_range', e.target.value)} />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>Weekdays</Label>
              <div className="flex flex-wrap gap-2">
                {DAY_OPTIONS.map((d) => {
                  const checked = (form.weekdays ?? []).includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => {
                        const current = form.weekdays ?? [];
                        setForm((f) => ({
                          ...f,
                          weekdays: checked
                            ? current.filter((v) => v !== d.value)
                            : [...current, d.value].sort((a, b) => a - b),
                        }));
                      }}
                      className={`px-3 py-1 rounded-md text-sm font-medium border transition-colors ${
                        checked
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-input hover:bg-muted'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>Holidays treated as off-peak</Label>
              <p className="text-xs text-muted-foreground">
                Select which public holidays (for {form.state || 'this state'}) count as off-peak. Matched by name, so the same selection applies every year.
              </p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={holidayNameOptions.length === 0}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">
                      {(form.holiday_names ?? []).length > 0
                        ? formatHolidayNames(form.holiday_names ?? [])
                        : holidayNameOptions.length === 0
                          ? 'No public holidays found for this state'
                          : 'Select holidays…'}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] max-h-64 overflow-y-auto">
                  <DropdownMenuLabel>Public holidays — {form.state}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {holidayNameOptions.map((name) => {
                    const checked = (form.holiday_names ?? []).includes(name);
                    return (
                      <DropdownMenuCheckboxItem
                        key={name}
                        checked={checked}
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={(next) => {
                          const current = form.holiday_names ?? [];
                          setForm((f) => ({
                            ...f,
                            holiday_names: next
                              ? [...current, name]
                              : current.filter((v) => v !== name),
                          }));
                        }}
                      >
                        {name}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-last_updated">Last updated</Label>
              <Input id="fld-last_updated" type="date" value={field('last_updated')} onChange={(e) => setField('last_updated', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-notes">Notes</Label>
              <Input id="fld-notes" value={field('notes')} onChange={(e) => setField('notes', e.target.value)} />
            </div>
          </div>

          {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TouRatesManagement;
